import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Plus, Trash2, Pencil, ExternalLink, Upload, Sparkles, AlertTriangle, ShieldCheck } from "lucide-react";
import {
  adminListVacancies,
  upsertVacancy,
  deleteVacancy,
  adminExtractVacancyFromImage,
} from "@/lib/api/vacancies.functions";
import { matchVacancyToUsers } from "@/lib/api/job-matches.functions";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/portal/admin/careers")({
  component: AdminCareersPage,
});

type Vacancy = {
  id: string;
  title: string;
  employer: string;
  location: string | null;
  sector: string | null;
  employment_type: string | null;
  description: string | null;
  requirements: string | null;
  application_url: string;
  source_name: string;
  source_url: string | null;
  salary_range: string | null;
  closing_date: string | null;
  is_published: boolean;
  posted_at: string;
};

const EMPTY: Partial<Vacancy> = {
  title: "",
  employer: "",
  location: "",
  sector: "",
  employment_type: "Full-time",
  description: "",
  requirements: "",
  application_url: "",
  source_name: "",
  source_url: "",
  salary_range: "",
  closing_date: "",
  is_published: true,
};

function AdminCareersPage() {
  const list = useServerFn(adminListVacancies);
  const upsert = useServerFn(upsertVacancy);
  const remove = useServerFn(deleteVacancy);
  const matchUsers = useServerFn(matchVacancyToUsers);
  const qc = useQueryClient();

  // Push a published vacancy into the portals of users whose skills match it.
  async function notifyMatches(vacancyId: string) {
    try {
      const r: any = await matchUsers({ data: { vacancyId, notify: true } });
      if (r?.matched > 0) {
        toast.success(
          `Sent to ${r.matched} matching user${r.matched === 1 ? "" : "s"} (${r.notified} emailed).`,
        );
      } else {
        toast.info("No users currently match this vacancy's skills or field.");
      }
    } catch (e: any) {
      toast.error(`Job matching failed: ${e?.message ?? "unknown error"}`);
    }
  }

  const { data, isLoading } = useQuery({
    queryKey: ["admin-vacancies"],
    queryFn: () => list(),
  });

  const [form, setForm] = useState<Partial<Vacancy>>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [aiSource, setAiSource] = useState<"manual" | "ai_image">("manual");
  const [verified, setVerified] = useState(false);
  const [aiWarnings, setAiWarnings] = useState<string[]>([]);
  const [aiConfidence, setAiConfidence] = useState<number | null>(null);

  const extract = useServerFn(adminExtractVacancyFromImage);
  const extractMut = useMutation({
    mutationFn: async (file: File) => {
      if (!file.type.startsWith("image/")) throw new Error("Please choose an image file.");
      if (file.size > 12 * 1024 * 1024) throw new Error("Image must be under 12 MB.");
      const buf = new Uint8Array(await file.arrayBuffer());
      let bin = "";
      const chunk = 0x8000;
      for (let i = 0; i < buf.length; i += chunk) bin += String.fromCharCode(...buf.subarray(i, i + chunk));
      return extract({ data: { fileName: file.name, fileMime: file.type, fileBase64: btoa(bin) } });
    },
    onSuccess: (r) => {
      setForm((prev) => ({
        ...prev,
        title: r.title || prev.title,
        employer: r.employer || prev.employer,
        location: r.location || prev.location,
        sector: r.sector || prev.sector,
        employment_type: r.employment_type || prev.employment_type,
        salary_range: r.salary_range || prev.salary_range,
        closing_date: r.closing_date || prev.closing_date,
        application_url: r.application_url || prev.application_url,
        source_name: r.source_name || prev.source_name,
        source_url: r.source_url || prev.source_url,
        description: r.description || prev.description,
        requirements: r.requirements || prev.requirements,
        is_published: false,
      }));
      setAiSource("ai_image");
      setVerified(false);
      setAiWarnings(r.warnings ?? []);
      setAiConfidence(r.confidence ?? null);
    },
  });

  const saveMut = useMutation({
    mutationFn: async (payload: any) => {
      const res: any = await upsert({ data: payload });
      if (payload.is_published && res?.id) await notifyMatches(res.id);
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-vacancies"] });
      qc.invalidateQueries({ queryKey: ["public-vacancies"] });
      setForm(EMPTY);
      setEditingId(null);
      setAiSource("manual");
      setVerified(false);
      setAiWarnings([]);
      setAiConfidence(null);
    },
  });

  const delMut = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-vacancies"] });
      qc.invalidateQueries({ queryKey: ["public-vacancies"] });
    },
  });

  const publishMut = useMutation({
    mutationFn: async (v: Vacancy & { _publish: boolean }) => {
      const payload: any = {
        id: v.id,
        title: v.title,
        employer: v.employer,
        location: v.location || null,
        sector: v.sector || null,
        employment_type: v.employment_type || null,
        description: v.description || null,
        requirements: v.requirements || null,
        application_url: v.application_url,
        source_name: v.source_name,
        source_url: v.source_url || null,
        salary_range: v.salary_range || null,
        closing_date: v.closing_date || null,
        is_published: v._publish,
      };
      const res = await upsert({ data: payload });
      if (v._publish) await notifyMatches(v.id);
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-vacancies"] });
      qc.invalidateQueries({ queryKey: ["public-vacancies"] });
    },
  });

  function startEdit(v: Vacancy) {
    setEditingId(v.id);
    setForm({ ...v });
    setAiSource("manual");
    setVerified(true);
    setAiWarnings([]);
    setAiConfidence(null);
  }

  const publishBlocked = aiSource === "ai_image" && !verified;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: any = { ...form };
    Object.keys(payload).forEach((k) => {
      if (payload[k] === "") payload[k] = null;
    });
    payload.title = form.title;
    payload.employer = form.employer;
    payload.application_url = form.application_url;
    payload.source_name = form.source_name;
    // Force draft if AI-extracted and not yet verified by admin
    payload.is_published = publishBlocked ? false : (form.is_published ?? true);
    if (editingId) payload.id = editingId;
    else delete payload.id;
    saveMut.mutate(payload);
  }


  const vacancies = (data ?? []) as Vacancy[];

  return (
    <div className="space-y-8">
      <header>
        <h2 className="font-display text-2xl text-navy">Careers & vacancies</h2>
        <p className="mt-1 text-sm text-foreground/70">
          Curate vacancies from legitimate SA sources. Public page: /careers
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="space-y-3 rounded-lg border border-border bg-card p-5"
      >
        <h3 className="font-medium text-navy">
          {editingId ? "Edit vacancy" : "Add vacancy"}
        </h3>

        {/* AI extract from picture */}
        <div className="rounded-md border border-dashed border-navy/30 bg-navy/5 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <Sparkles className="h-4 w-4 text-navy" />
            <span className="text-sm font-medium text-navy">Extract vacancy from a picture</span>
            <label className="ml-auto inline-flex cursor-pointer items-center gap-1 rounded border border-navy/40 bg-white px-2 py-1 text-xs hover:border-navy">
              <Upload className="h-3 w-3" />
              {extractMut.isPending ? "Reading…" : "Upload poster image"}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) extractMut.mutate(f);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          <p className="mt-1 text-[11px] text-muted-foreground">
            AI reads the poster (JPG/PNG/WEBP, ≤12 MB), pre-fills the form and marks it as a
            draft until you verify it below.
          </p>
          {extractMut.error ? (
            <p className="mt-1 text-xs text-red-600">{(extractMut.error as Error).message}</p>
          ) : null}
          {aiSource === "ai_image" && (aiWarnings.length > 0 || aiConfidence !== null) ? (
            <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
              <p className="flex items-center gap-1 font-medium">
                <AlertTriangle className="h-3 w-3" /> Review required
                {aiConfidence !== null && (
                  <span className="ml-auto text-[10px] uppercase tracking-wider">
                    Confidence {Math.round(aiConfidence * 100)}%
                  </span>
                )}
              </p>
              {aiWarnings.length > 0 && (
                <ul className="ml-4 mt-1 list-disc">
                  {aiWarnings.map((w, i) => (
                    <li key={i}>{w}</li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Title *">
            <input
              required
              value={form.title ?? ""}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
            />
          </Field>
          <Field label="Employer *">
            <input
              required
              value={form.employer ?? ""}
              onChange={(e) => setForm({ ...form, employer: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
            />
          </Field>
          <Field label="Location">
            <input
              value={form.location ?? ""}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              placeholder="e.g. Pretoria, Gauteng"
            />
          </Field>
          <Field label="Sector">
            <input
              value={form.sector ?? ""}
              onChange={(e) => setForm({ ...form, sector: e.target.value })}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              placeholder="e.g. Public service, Legal, Finance"
            />
          </Field>
          <Field label="Employment type">
            <select
              value={form.employment_type ?? ""}
              onChange={(e) =>
                setForm({ ...form, employment_type: e.target.value })
              }
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
            >
              <option value="">—</option>
              <option>Full-time</option>
              <option>Part-time</option>
              <option>Contract</option>
              <option>Internship</option>
              <option>Learnership</option>
              <option>Temporary</option>
            </select>
          </Field>
          <Field label="Salary range">
            <input
              value={form.salary_range ?? ""}
              onChange={(e) =>
                setForm({ ...form, salary_range: e.target.value })
              }
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              placeholder="e.g. R15 000 – R22 000 p/m"
            />
          </Field>
          <Field label="Application URL *">
            <input
              required
              type="url"
              value={form.application_url ?? ""}
              onChange={(e) =>
                setForm({ ...form, application_url: e.target.value })
              }
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              placeholder="https://..."
            />
          </Field>
          <Field label="Closing date">
            <input
              type="date"
              value={form.closing_date ?? ""}
              onChange={(e) =>
                setForm({ ...form, closing_date: e.target.value })
              }
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
            />
          </Field>
          <Field label="Source name *">
            <input
              required
              value={form.source_name ?? ""}
              onChange={(e) =>
                setForm({ ...form, source_name: e.target.value })
              }
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              placeholder="e.g. DPSA Circular 42 of 2026"
            />
          </Field>
          <Field label="Source URL">
            <input
              type="url"
              value={form.source_url ?? ""}
              onChange={(e) =>
                setForm({ ...form, source_url: e.target.value })
              }
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
              placeholder="https://www.dpsa.gov.za/..."
            />
          </Field>
        </div>
        <Field label="Description">
          <textarea
            rows={3}
            value={form.description ?? ""}
            onChange={(e) =>
              setForm({ ...form, description: e.target.value })
            }
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
          />
        </Field>
        <Field label="Requirements">
          <textarea
            rows={2}
            value={form.requirements ?? ""}
            onChange={(e) =>
              setForm({ ...form, requirements: e.target.value })
            }
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-navy focus:outline-none"
          />
        </Field>
        {aiSource === "ai_image" && (
          <label className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-2 text-xs text-emerald-900">
            <input
              type="checkbox"
              checked={verified}
              onChange={(e) => setVerified(e.target.checked)}
              className="mt-0.5"
            />
            <span>
              <span className="flex items-center gap-1 font-medium">
                <ShieldCheck className="h-3 w-3" /> I have verified every AI-extracted field
                against the poster (title, employer, application URL, closing date, salary).
              </span>
              Publish is disabled until this is ticked.
            </span>
          </label>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={(form.is_published ?? true) && !publishBlocked}
            disabled={publishBlocked}
            onChange={(e) =>
              setForm({ ...form, is_published: e.target.checked })
            }
          />
          Published (visible on /careers)
          {publishBlocked && (
            <span className="text-xs text-amber-700">— tick the verification box first</span>
          )}
        </label>
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saveMut.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-navy px-4 py-2 text-sm font-medium text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
          >
            <Plus className="h-4 w-4" />
            {editingId ? "Save changes" : publishBlocked ? "Save as draft" : "Add vacancy"}
          </button>

          {editingId && (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setForm(EMPTY);
              }}
              className="rounded-md border border-input px-3 py-2 text-sm hover:border-navy"
            >
              Cancel
            </button>
          )}
        </div>
        {saveMut.error && (
          <p className="text-xs text-red-600">
            {(saveMut.error as Error).message}
          </p>
        )}
      </form>

      <section>
        <h3 className="mb-3 font-medium text-navy">Vacancies ({vacancies.length})</h3>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : vacancies.length === 0 ? (
          <p className="text-sm text-muted-foreground">No vacancies yet.</p>
        ) : (
          <ul className="space-y-2">
            {vacancies.map((v) => (
              <li
                key={v.id}
                className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-border bg-card p-4"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-navy">{v.title}</p>
                    {!v.is_published && (
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase">
                        Draft
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-foreground/80">
                    {v.employer}
                    {v.location && ` • ${v.location}`}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Source: {v.source_name}
                    {v.closing_date && ` • Closes ${v.closing_date}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={v.application_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded border border-input px-2 py-1 text-xs hover:border-navy"
                  >
                    <ExternalLink className="h-3 w-3" /> Open
                  </a>
                  {v.is_published ? (
                    <button
                      disabled={publishMut.isPending}
                      onClick={() => publishMut.mutate({ ...v, _publish: false })}
                      className="inline-flex items-center gap-1 rounded border border-input px-2 py-1 text-xs hover:border-navy disabled:opacity-60"
                    >
                      Unpublish
                    </button>
                  ) : (
                    <button
                      disabled={publishMut.isPending}
                      onClick={() => publishMut.mutate({ ...v, _publish: true })}
                      className="inline-flex items-center gap-1 rounded border border-emerald-500 bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
                    >
                      Publish now
                    </button>
                  )}
                  <button
                    onClick={() => startEdit(v)}
                    className="inline-flex items-center gap-1 rounded border border-input px-2 py-1 text-xs hover:border-navy"
                  >
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  <button
                    onClick={() => {
                      if (confirm("Delete this vacancy?")) delMut.mutate(v.id);
                    }}
                    className="inline-flex items-center gap-1 rounded border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-xs">
      <span className="mb-1 block font-medium text-foreground/80">{label}</span>
      {children}
    </label>
  );
}
